import { TableSkeleton } from '@/components/ui/TableSkeleton';
import { SegmentsSkeleton } from '../../pages/marketing/segment-builder/SegmentsListSkeleton';
import { SegmentEntrySkeleton, SegmentWorkspaceSkeleton } from '../../pages/marketing/segment-builder/SegmentLoading';

const WORKSPACE_PATHS = [
  /^\/contacts\/(?:new|[^/]+(?:\/edit)?)$/,
  /^\/payments\/(?:new(?:\/[^/]+)?|[^/]+(?:\/edit)?)$/,
  /^\/subscriptions\/[^/]+(?:\/edit)?$/,
  /^\/forms\/(?:new(?:\/[^/]+)?|[^/]+\/edit|[^/]+\/submissions\/[^/]+)$/,
  /^\/elements\/(?:new(?:\/[^/]+)?|[^/]+\/edit)$/,
  /^\/broadcasts\/(?:new(?:\/.*)?|[^/]+(?:\/(?:details|payments|edit))?)$/,
  /^\/segments\/(?:new|[^/]+(?:\/edit)?)$/,
];

const TABLE_PATHS = [
  /^\/contacts$/,
  /^\/payments$/,
  /^\/subscriptions$/,
  /^\/forms(?:\/[^/]+)?$/,
  /^\/elements(?:\/[^/]+)?$/,
  /^\/payouts(?:\/[^/]+)?$/,
  /^\/broadcasts$/,
];

function normalizeDashboardPath(pathname = '/') {
  const path = String(pathname).split(/[?#]/, 1)[0] || '/';
  const withoutDashboardBase = path.replace(/^\/dashboard(?=\/|$)/, '') || '/';
  return withoutDashboardBase.length > 1
    ? withoutDashboardBase.replace(/\/+$/, '')
    : withoutDashboardBase;
}

export function getDashboardRouteSkeletonKind(pathname) {
  const path = normalizeDashboardPath(pathname);

  if (path === '/') return 'analytics';
  if (path === '/settings' || path.startsWith('/settings/')) return 'settings';
  if (path === '/segments') return 'cards';
  if (path === '/brand-kit') return 'editor';
  if (WORKSPACE_PATHS.some((pattern) => pattern.test(path))) return 'workspace';
  if (TABLE_PATHS.some((pattern) => pattern.test(path))) return 'table';
  return 'neutral';
}

function SkeletonLine({ className = '' }) {
  return <div className={`animate-pulse rounded bg-paper-inset ${className}`} />;
}

function AnalyticsSkeleton() {
  return (
    <main className="flex min-h-0 flex-1 flex-col overflow-hidden px-4 lg:pl-0">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="rounded-md border border-edge bg-white p-4">
            <SkeletonLine className="h-3 w-24" />
            <SkeletonLine className="mt-4 h-7 w-32" />
            <SkeletonLine className="mt-3 h-3 w-40 max-w-full" />
          </div>
        ))}
      </div>
      <div className="mt-3 grid min-h-0 flex-1 gap-3 lg:grid-cols-2">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="min-h-48 rounded-md border border-edge bg-white p-4">
            <SkeletonLine className="h-4 w-36" />
            <SkeletonLine className="mt-2 h-3 w-52 max-w-full" />
            <div className="mt-7 flex h-28 items-end gap-2">
              {[45, 72, 54, 88, 64, 80, 52, 70].map((height, barIndex) => (
                <div
                  key={barIndex}
                  className="flex-1 animate-pulse rounded-t bg-paper-inset"
                  style={{ height: `${height}%` }}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </main>
  );
}

// PE-2008 — must stay visually identical to the skeleton the table screens
// render once their lazy chunk has loaded (DonorsTable / DonationsTable /
// ElementsTable / PayoutsTable / SubscriptionsTable / BroadcastsPage all pass
// header + filter bar + footer, 20 rows, 9 columns). If the two diverge, a
// cold load visibly swaps one skeleton for another and reads as two separate
// loading screens. Keep these props in sync with those call sites.
function TableRouteSkeleton() {
  return (
    <main className="min-h-0 flex-1 overflow-hidden px-4 lg:pl-0">
      <div className="overflow-hidden rounded-md border border-edge bg-white">
        <TableSkeleton showHeader showFilterBar showFooter rowCount={20} columnCount={9} />
      </div>
    </main>
  );
}

function SettingsSkeleton() {
  return (
    <main className="flex min-h-0 flex-1 flex-col overflow-hidden px-4 lg:pl-0">
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-md border border-edge bg-white">
        <div className="border-b border-edge px-5 py-5 sm:px-6">
          <SkeletonLine className="h-6 w-28" />
          <SkeletonLine className="mt-2 h-3 w-96 max-w-full" />
        </div>
        <div className="grid min-h-0 flex-1 lg:grid-cols-[208px_minmax(0,1fr)] min-[1150px]:grid-cols-[260px_minmax(0,1fr)]">
          <aside className="hidden border-r border-edge p-4 lg:block">
            {Array.from({ length: 10 }).map((_, index) => (
              <div key={index} className="mb-4">
                <SkeletonLine className={index % 4 === 0 ? 'h-3 w-20' : 'h-8 w-full'} />
              </div>
            ))}
          </aside>
          <section className="min-w-0">
            <div className="border-b border-edge px-5 py-4 sm:px-6">
              <SkeletonLine className="h-5 w-32" />
              <SkeletonLine className="mt-2 h-3 w-64 max-w-full" />
            </div>
            <div className="grid gap-5 p-5 sm:grid-cols-2 sm:p-6">
              {Array.from({ length: 8 }).map((_, index) => (
                <div key={index}>
                  <SkeletonLine className="h-3 w-24" />
                  <SkeletonLine className="mt-2 h-9 w-full" />
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </main>
  );
}

function WorkspaceSkeleton({ editor = false }) {
  return (
    <main className="min-h-0 flex-1 overflow-hidden px-4 lg:pl-0">
      <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-md border border-edge bg-white">
        <div className="border-b border-edge bg-paper-light px-5 py-6">
          <SkeletonLine className="h-7 w-64 max-w-full" />
          <SkeletonLine className="mt-3 h-3 w-80 max-w-full" />
        </div>
        <div
          className={`grid min-h-0 flex-1 gap-4 overflow-hidden p-4 ${editor ? 'lg:grid-cols-[minmax(0,1fr)_360px]' : 'lg:grid-cols-2'}`}
        >
          {Array.from({ length: editor ? 2 : 4 }).map((_, index) => (
            <section key={index} className="rounded-md border border-edge bg-white p-5">
              <SkeletonLine className="h-4 w-32" />
              {Array.from({ length: editor ? 7 : 4 }).map((__, rowIndex) => (
                <div key={rowIndex} className="mt-5">
                  <SkeletonLine className="h-3 w-24" />
                  <SkeletonLine className="mt-2 h-8 w-full" />
                </div>
              ))}
            </section>
          ))}
        </div>
      </div>
    </main>
  );
}

function NeutralSkeleton() {
  return (
    <main className="min-h-0 flex-1 overflow-hidden px-4 lg:pl-0">
      <div className="h-full rounded-md border border-edge bg-white p-6">
        <SkeletonLine className="h-7 w-48" />
        <SkeletonLine className="mt-3 h-3 w-96 max-w-full" />
        <SkeletonLine className="mt-8 h-48 w-full" />
      </div>
    </main>
  );
}

export default function DashboardRouteFallback({ pathname, header = null }) {
  const kind = getDashboardRouteSkeletonKind(pathname);
  const path = normalizeDashboardPath(pathname);
  const label = `Loading ${kind === 'neutral' ? 'dashboard page' : kind}`;

  return (
    <>
      {header}
      <div
        className="flex min-h-0 flex-1 flex-col bg-paper-muted"
        role="status"
        aria-busy="true"
        aria-label={label}
        data-dashboard-route-skeleton={kind}
      >
        {kind === 'analytics' && <AnalyticsSkeleton />}
        {kind === 'table' && <TableRouteSkeleton />}
        {kind === 'settings' && <SettingsSkeleton />}
        {kind === 'cards' && <main className="flex min-h-0 flex-1 flex-col overflow-hidden px-4 lg:pl-0"><SegmentsSkeleton /></main>}
        {kind === 'workspace' && (path.startsWith('/segments/')
          ? <main className="flex min-h-0 flex-1 flex-col overflow-hidden px-4 lg:pl-0">{path === '/segments/new' ? <SegmentEntrySkeleton crumbs={[{ label: 'Segments' }, { label: 'New segment' }]} /> : <SegmentWorkspaceSkeleton mode={path.endsWith('/edit') ? 'edit' : 'view'} crumbs={[{ label: 'Segments' }, { label: '…' }]} />}</main>
          : <WorkspaceSkeleton />)}
        {kind === 'editor' && <WorkspaceSkeleton editor />}
        {kind === 'neutral' && <NeutralSkeleton />}
      </div>
    </>
  );
}
